import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Modal } from './Modal';
import { STRAINS } from '../../game/data/strains';
import { buttonHover, buttonTap } from '../../ui/animations';

const RARITY_COLORS = {
  common:   'var(--green)',
  uncommon: 'var(--blue)',
  rare:     '#d29922',
};

const RARITY_LABELS = {
  common:   'Common',
  uncommon: 'Uncommon',
  rare:     'Rare ✨',
};

const STAT_LABELS = { yield: 'Yield', thc: 'THC', terpene: 'Terpenes' };

function formatBonus(stat, value) {
  return `+${Math.round(value * 100)}% ${STAT_LABELS[stat] || stat}`;
}

export function PhenoHuntModal({ phenoCandidates, dispatch }) {
  const [selectedIdx, setSelectedIdx] = useState(null);
  const [name, setName] = useState('');

  if (!phenoCandidates) return null;

  const strain = STRAINS[phenoCandidates.strainId];
  const { candidates, batchQuality } = phenoCandidates;
  const trimmedName = name.trim();
  const canSave = selectedIdx !== null && trimmedName.length > 0 && trimmedName.length <= 24;

  function handleSave() {
    if (!canSave) return;
    dispatch({
      type: 'SAVE_PHENO',
      payload: { candidateIndex: selectedIdx, name: trimmedName },
    });
  }

  function handleDismiss() {
    dispatch({ type: 'DISMISS_PHENO_CANDIDATES' });
  }

  return (
    <Modal title="🧬 Phenotype Candidates" onClose={handleDismiss}>
      {/* Header */}
      <div style={{ textAlign: 'center', marginBottom: 14 }}>
        <div style={{ fontSize: 14, fontWeight: 700 }}>{strain?.name}</div>
        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>
          Harvest quality: <span style={{ color: 'var(--blue)', fontWeight: 600 }}>{batchQuality}/100</span>
          {' · '}Select a pheno to save to your library
        </div>
      </div>

      {/* Candidate cards */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
        {candidates.map((c, i) => (
          <motion.div
            key={i}
            onClick={() => setSelectedIdx(i)}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            style={{
              flex: 1,
              padding: '10px 8px',
              borderRadius: 'var(--radius)',
              border: `2px solid ${selectedIdx === i ? RARITY_COLORS[c.rarity] : 'var(--border)'}`,
              background: selectedIdx === i ? 'var(--border-light)' : 'var(--bg)',
              cursor: 'pointer',
              transition: 'border-color 0.15s',
              minWidth: 0,
            }}
          >
            <div style={{
              fontSize: 11,
              fontWeight: 700,
              color: RARITY_COLORS[c.rarity],
              marginBottom: 6,
            }}>
              {RARITY_LABELS[c.rarity]}
            </div>
            {Object.entries(c.bonuses).map(([stat, val]) => (
              <div key={stat} style={{ fontSize: 12, color: 'var(--green)', marginBottom: 2 }}>
                {formatBonus(stat, val)}
              </div>
            ))}
          </motion.div>
        ))}
      </div>

      {/* Name input */}
      <div style={{ marginBottom: 14 }}>
        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 4 }}>
          Name this phenotype <span style={{ color: 'var(--text-muted)' }}>(max 24 chars)</span>
        </div>
        <input
          type="text"
          value={name}
          onChange={e => setName(e.target.value)}
          placeholder={`${strain?.name?.split(' ')[0] || 'Pheno'} #1`}
          maxLength={24}
          style={{
            width: '100%',
            background: 'var(--border-light)',
            border: '1px solid var(--border)',
            borderRadius: 'var(--radius-sm)',
            color: 'var(--text)',
            padding: '6px 10px',
            fontSize: 13,
            boxSizing: 'border-box',
          }}
          onKeyDown={e => { if (e.key === 'Enter') handleSave(); }}
        />
      </div>

      {/* Actions */}
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <motion.button
          className="btn btn-primary"
          style={{ flex: 1 }}
          disabled={!canSave}
          onClick={handleSave}
          whileHover={canSave ? buttonHover : {}}
          whileTap={canSave ? buttonTap : {}}
        >
          Save Pheno
        </motion.button>
        <button
          className="btn btn-secondary"
          onClick={handleDismiss}
          style={{ fontSize: 12 }}
        >
          Skip
        </button>
      </div>
    </Modal>
  );
}
